import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Dialog, DialogActions, Field, FilterBar, Select, Table } from "@studio";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { fmtDate, fmtDateTime, num, serviceNames, serviceTone } from "../lib/format";
import { useAction, useCustomers, usePlans } from "../lib/queries";
import type { CustomerSummary, Me, Plan, Subscription } from "../lib/types";
import { Empty, ErrorBox, InfoRow, Loading, Panel, StatGrid, Tabs } from "../console/ui";
import { MaskedValue } from "../console/masked";

/* ---------- 타입·조회 (결제 화면 전용) ---------- */

type Transfer = { id: string; status: "declared" | "approved" | "partial" | "rejected"; declaredAmount: number; depositorName: string; depositedAt: string; declaredAt: string; note: string | null } | null;
type TaxInvoiceRef = { id: string; status: "requested" | "issued" | "cancelled"; approvalNo: string | null; issuedAt: string | null; email: string } | null;
export type Invoice = {
  id: string;
  number: string;
  customerId: string;
  customerName?: string;
  planCode: string;
  planName: string | null;
  period: "month" | "year";
  lines: { kind: string; label: string; amount: number }[];
  supply: number;
  vat: number;
  total: number;
  status: "open" | "pending_confirmation" | "paid" | "overdue" | "void";
  issuedAt: string;
  dueAt: string;
  paidAt: string | null;
  appliedPeriodStart: string | null;
  appliedPeriodEnd: string | null;
  memo: string | null;
  transfer: Transfer;
  taxInvoice: TaxInvoiceRef;
};
type Payment = { id: string; invoiceNumber: string; amount: number; method: string; status: string; confirmedAt: string | null; createdAt: string };
type CustomerBilling = { subscription: Subscription; guide: { account: string; vatRatePct: number; dueDays: number }; invoices: Invoice[]; payments: Payment[] };
type Approvals = {
  transfers: { id: string; invoiceId: string; invoiceNumber: string; invoiceTotal: number; customerId: string; customerName: string; declaredAmount: number; depositorName: string; depositedAt: string; declaredAt: string }[];
  taxInvoices: { id: string; invoiceId: string; invoiceNumber: string; invoiceStatus: string; customerName: string; bizNo: string; corpName: string; ceoName: string; email: string; address: string | null; bizType: string | null; supply: number; vat: number; createdAt: string }[];
};

const won = (n: number) => `${num(n)}원`;
const invoiceStatus: Record<Invoice["status"], [string, string]> = {
  open: ["미납", "amber"],
  pending_confirmation: ["입금 확인 중", "blue"],
  paid: ["결제 완료", "teal"],
  overdue: ["기한 지남", "coral"],
  void: ["취소", "neutral"],
};
const methodNames: Record<string, string> = { transfer: "계좌이체", card: "카드", vbank: "가상계좌", cms: "자동이체", postpaid: "후불", manual: "직접 처리" };
const bizNoText = (s: string) => (s.length === 10 ? `${s.slice(0, 3)}-${s.slice(3, 5)}-${s.slice(5)}` : s);

const useCustomerBilling = (customerId: string) =>
  useQuery({ queryKey: ["billing", customerId], queryFn: () => api.get<CustomerBilling>(`/c/${customerId}/billing`), refetchInterval: 30_000 });
const useAdminInvoices = (f: { status?: string; customerId?: string }) =>
  useQuery({
    queryKey: ["billing", "admin", "invoices", f],
    queryFn: () => api.page<Invoice>(`/admin/billing/invoices?limit=100${f.status ? `&status=${f.status}` : ""}${f.customerId ? `&customerId=${f.customerId}` : ""}`),
    refetchInterval: 30_000,
  });
const useApprovals = () => useQuery({ queryKey: ["billing", "admin", "approvals"], queryFn: () => api.get<Approvals>("/admin/billing/approvals"), refetchInterval: 15_000 });

function InvoiceStatusBadge({ inv }: { inv: Invoice }) {
  const [label, tone] = invoiceStatus[inv.status];
  return <Badge tone={tone}>{label}</Badge>;
}

function InvoiceLines({ inv }: { inv: Invoice }) {
  return (
    <dl className="mcc-kv">
      {inv.lines.map((l, i) => (
        <span key={i} style={{ display: "contents" }}>
          <dt>{l.label}</dt>
          <dd>{won(l.amount)}</dd>
        </span>
      ))}
      <dt>공급가액</dt>
      <dd>{won(inv.supply)}</dd>
      <dt>부가세</dt>
      <dd>{won(inv.vat)}</dd>
      <dt>
        <strong>합계</strong>
      </dt>
      <dd>
        <strong>{won(inv.total)}</strong>
      </dd>
      <dt>납부 기한</dt>
      <dd>{fmtDate(inv.dueAt)}</dd>
      {inv.appliedPeriodEnd && (
        <>
          <dt>적용 기간</dt>
          <dd>
            {fmtDate(inv.appliedPeriodStart)} ~ {fmtDate(inv.appliedPeriodEnd)}
          </dd>
        </>
      )}
      {inv.memo && (
        <>
          <dt>메모</dt>
          <dd>{inv.memo}</dd>
        </>
      )}
    </dl>
  );
}

/* ---------- 고객 화면: 사용량·결제 > 결제 ---------- */

export function CustomerBillingScreen({ me, customer }: { me: Me; customer: CustomerSummary }) {
  const q = useCustomerBilling(customer.id);
  const [tab, setTab] = useState("구독");
  const [declare, setDeclare] = useState<Invoice | null>(null);
  const [tax, setTax] = useState<Invoice | null>(null);
  const canPay = can(me, "BILLING_PAY");
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={errorMessage(q.error)} onRetry={() => void q.refetch()} />;
  const { subscription: sub, guide, invoices, payments } = q.data;
  const unpaid = invoices.filter((i) => i.status === "open" || i.status === "overdue");
  return (
    <>
      <StatGrid
        links={{
          // 같은 화면의 탭으로 (2026-10-21)
          "구독 기간": { onOpen: () => setTab("구독"), label: "구독 탭에서 보기" },
          "다음 청구": { onOpen: () => setTab("청구서"), label: "청구서 탭에서 보기" },
        }}
        items={[
          ["서비스 상태", serviceNames[customer.status], customer.statusReason ?? "", serviceTone[customer.status]],
          ["구독 기간", sub ? `${sub.daysLeft}일` : "-", sub ? `${sub.planName ?? sub.planCode} · ${fmtDate(sub.currentPeriodEnd)}까지` : "플랜 없음"],
          ["다음 청구", unpaid.length ? won(unpaid[0].total) : "없음", unpaid.length ? `${fmtDate(unpaid[0].dueAt)}까지` : sub?.promotion ? `프로모션 · ${fmtDate(sub.currentPeriodEnd)}까지 무료` : "미납 청구서 없음", unpaid.length ? "amber" : "teal"],
        ]}
      />
      {/* 시안 billing-current BillingView: 구독 · 청구서 · 세금계산서 · 결제 이력 */}
      <Tabs items={["구독", "청구서", "세금계산서", "결제 이력"]} value={tab} onChange={setTab} />
      {tab === "구독" ? (
        <>
          <Panel title={sub ? `${sub.planName ?? sub.planCode} ${sub.period === "year" ? "연간" : "월간"} 구독 · ${fmtDate(sub.currentPeriodEnd)}까지` : "구독"}>
            {sub?.promotion && (
              <InfoRow
                title={`프로모션 · ${fmtDate(sub.currentPeriodEnd)}까지 무료 이용`}
                description={sub.endingSoon ? "프로모션이 곧 끝납니다. 계속 이용하려면 담당자에게 결제를 요청해 주세요. 미리 결제하면 프로모션이 끝나는 날부터 이어집니다." : "프로와 같은 기능·한도를 결제 없이 이용합니다."}
              />
            )}
            <InfoRow title="기간 내 서비스 이용" description={sub ? `남은 기간 ${sub.daysLeft}일 · 기간제 이용료 (작업 횟수 제한 없음)` : "이용 중인 플랜이 없습니다. 담당자에게 문의해 주세요."} />
            <InfoRow title="다음 청구" description={unpaid.length ? `${unpaid[0].number} · ${won(unpaid[0].total)} · ${fmtDate(unpaid[0].dueAt)}까지` : "미납 청구서 없음"} />
            {customer.support && <InfoRow title="결제 문의" description={customer.support} />}
          </Panel>
          {unpaid.length > 0 && (
            <Panel title="계좌이체 안내">
              <InfoRow title={guide.account || "입금 계좌는 담당자에게 문의해 주세요"} description="입금 후 청구서 탭에서 '입금 신고'를 눌러 주세요. 담당자 확인 후 바로 반영됩니다." />
            </Panel>
          )}
        </>
      ) : tab === "세금계산서" ? (
        <Panel title="세금계산서 발행 상태">
          {invoices.filter((i) => i.status !== "void").length === 0 && <InfoRow title="발행할 청구서가 없습니다" />}
          {invoices
            .filter((i) => i.status !== "void")
            .map((inv) => (
              <InfoRow
                key={inv.id}
                title={`${inv.number} · ${inv.taxInvoice ? (inv.taxInvoice.status === "issued" ? "발행 완료" : "발행 요청됨") : "요청 전"}`}
                description={inv.taxInvoice ? (inv.taxInvoice.status === "issued" ? `승인번호 ${inv.taxInvoice.approvalNo} · ${fmtDate(inv.taxInvoice.issuedAt)} · ${inv.taxInvoice.email}` : `${inv.taxInvoice.email}로 발행 예정`) : `${won(inv.total)} · 요청하면 담당자가 발행합니다`}
              >
                {canPay && !inv.taxInvoice && (
                  <Button size="sm" variant="secondary" onClick={() => setTax(inv)}>
                    세금계산서 요청
                  </Button>
                )}
              </InfoRow>
            ))}
        </Panel>
      ) : tab === "청구서" ? (
        invoices.length === 0 ? (
          <Empty>청구서가 없습니다. 이용 중인 플랜의 청구서가 발행되면 여기에 표시됩니다.</Empty>
        ) : (
          invoices.map((inv) => (
            <Panel key={inv.id} title={`${inv.number} · ${inv.planName ?? inv.planCode} ${inv.period === "year" ? "연간" : "월간"}`} extra={<InvoiceStatusBadge inv={inv} />}>
              <InvoiceLines inv={inv} />
              {inv.transfer && (
                <InfoRow
                  title={
                    <>
                      입금 신고 ·{" "}
                      <MaskedValue
                        value={inv.transfer.depositorName}
                        source={{ url: `/c/${customer.id}/billing/invoices/${inv.id}/pii`, pick: (d) => (d.transfers as { id: string; depositorName: string }[] | undefined)?.find((x) => x.id === inv.transfer?.id)?.depositorName }}
                      />
                      {` · ${won(inv.transfer.declaredAmount)}`}
                    </>
                  }
                  description={`${fmtDateTime(inv.transfer.declaredAt)} 신고 · ${inv.transfer.status === "declared" ? "담당자 확인 중" : inv.transfer.status === "rejected" ? `반려: ${inv.transfer.note ?? ""}` : "확인 완료"}`}
                />
              )}
              {inv.taxInvoice && (
                <InfoRow
                  title={`세금계산서 · ${inv.taxInvoice.status === "issued" ? "발행 완료" : "발행 요청됨"}`}
                  description={inv.taxInvoice.status === "issued" ? `승인번호 ${inv.taxInvoice.approvalNo} · ${fmtDate(inv.taxInvoice.issuedAt)} · ${inv.taxInvoice.email}` : `${inv.taxInvoice.email}로 발행 예정`}
                />
              )}
              {canPay && (
                <div className="mcc-padding meta-crew-buttons">
                  {(inv.status === "open" || inv.status === "overdue") && <Button onClick={() => setDeclare(inv)}>입금 신고</Button>}
                  {inv.status !== "void" && !inv.taxInvoice && (
                    <Button variant="secondary" onClick={() => setTax(inv)}>
                      세금계산서 요청
                    </Button>
                  )}
                </div>
              )}
            </Panel>
          ))
        )
      ) : payments.length === 0 ? (
        <Empty>결제 이력이 없습니다.</Empty>
      ) : (
        <Panel title="결제 이력">
          <Table>
            <thead>
              <tr>
                <th>일시</th>
                <th>청구서</th>
                <th>금액</th>
                <th>수단</th>
                <th>상태</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDateTime(p.confirmedAt ?? p.createdAt)}</td>
                  <td>{p.invoiceNumber}</td>
                  <td>{won(p.amount)}</td>
                  <td>{methodNames[p.method] ?? p.method}</td>
                  <td>
                    <Badge tone={p.status === "confirmed" ? "teal" : "amber"}>{p.status === "confirmed" ? "확정" : p.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      )}
      <DeclareDialog invoice={declare} path={(id) => `/c/${customer.id}/billing/invoices/${id}/transfer`} onClose={() => setDeclare(null)} />
      <TaxDialog invoice={tax} customer={customer} onClose={() => setTax(null)} />
    </>
  );
}

function DeclareDialog({ invoice, path, onClose, title = "입금 신고", confirmLabel = "신고" }: { invoice: Invoice | null; path: (id: string) => string; onClose: () => void; title?: string; confirmLabel?: string }) {
  const formId = useId();
  const [error, setError] = useState<string | null>(null);
  const send = useAction((b: Record<string, unknown>) => api.post(path(invoice!.id), b), [["billing"], ["c"], ["admin"]]);
  const now = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  return (
    <Dialog
      open={!!invoice}
      onOpenChange={(v) => !v && onClose()}
      title={title}
      description={invoice ? `${invoice.number} · 합계 ${won(invoice.total)}` : ""}
      footer={<DialogActions secondary={{ label: "취소", onClick: onClose }} primary={{ label: confirmLabel, type: "submit", form: formId, disabled: send.isPending }} note={error ?? undefined} />}
    >
      {invoice && (
        <form
          id={formId}
          className="mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            const d = new FormData(e.currentTarget);
            send
              .mutateAsync({ depositorName: String(d.get("depositorName")).trim(), amount: Number(d.get("amount")), depositedAt: new Date(String(d.get("depositedAt"))).toISOString() })
              .then(onClose)
              .catch((er) => setError(errorMessage(er)));
          }}
        >
          <Field name="depositorName" label="입금자명" required maxLength={100} />
          <Field name="amount" label="입금 금액 (원)" type="number" min={1} defaultValue={invoice.total} required />
          <Field name="depositedAt" label="입금 일시" type="datetime-local" defaultValue={now} required />
        </form>
      )}
    </Dialog>
  );
}

function TaxDialog({ invoice, customer, onClose }: { invoice: Invoice | null; customer: CustomerSummary; onClose: () => void }) {
  const formId = useId();
  const [error, setError] = useState<string | null>(null);
  const send = useAction((b: Record<string, unknown>) => api.post(`/c/${customer.id}/billing/invoices/${invoice!.id}/tax-invoice`, b), [["billing"]]);
  return (
    <Dialog
      open={!!invoice}
      onOpenChange={(v) => !v && onClose()}
      title="세금계산서 요청"
      description="담당자가 발행 후 입력한 이메일로 보내드립니다."
      footer={<DialogActions secondary={{ label: "취소", onClick: onClose }} primary={{ label: "요청", type: "submit", form: formId, disabled: send.isPending }} note={error ?? undefined} />}
    >
      <form
        id={formId}
        className="mcc-form"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const d = new FormData(e.currentTarget);
          const v = (k: string) => String(d.get(k) ?? "").trim() || undefined;
          send
            .mutateAsync({ bizNo: v("bizNo"), corpName: v("corpName"), ceoName: v("ceoName"), email: v("email"), address: v("address"), bizType: v("bizType") })
            .then(onClose)
            .catch((er) => setError(errorMessage(er)));
        }}
      >
        <Field name="bizNo" label="사업자등록번호" placeholder="123-45-67890" defaultValue={customer.bizNo ?? ""} required />
        <Field name="corpName" label="상호" defaultValue={customer.name} required />
        <Field name="ceoName" label="대표자" required />
        <Field name="email" label="받을 이메일" type="email" required />
        <Field name="address" label="사업장 주소" />
        <Field name="bizType" label="업태·종목" />
      </form>
    </Dialog>
  );
}

/* ---------- 전체 관리: 결제 운영 ---------- */

export function AdminBillingScreen({ me, enter }: { me: Me; enter: (id: string, s?: string) => void }) {
  const [tab, setTab] = useState("승인 대기");
  const approvals = useApprovals();
  const pending = (approvals.data?.transfers.length ?? 0) + (approvals.data?.taxInvoices.length ?? 0);
  return (
    <>
      <Tabs items={[`승인 대기${pending ? ` ${pending}` : ""}`, "청구서", "구독", "플랜 가격"]} value={tab.startsWith("승인") ? `승인 대기${pending ? ` ${pending}` : ""}` : tab} onChange={(t) => setTab(t.startsWith("승인") ? "승인 대기" : t)} />
      {tab === "승인 대기" ? <ApprovalsPanel me={me} q={approvals} /> : tab === "청구서" ? <InvoicesPanel me={me} /> : tab === "구독" ? <SubscriptionsPanel enter={enter} /> : <PlanPricePanel me={me} />}
    </>
  );
}

function ApprovalsPanel({ me, q }: { me: Me; q: ReturnType<typeof useApprovals> }) {
  const canManage = can(me, "BILLING_MANAGE");
  const [msg, setMsg] = useState<string | null>(null);
  const [reject, setReject] = useState<string | null>(null);
  const [issue, setIssue] = useState<Approvals["taxInvoices"][number] | null>(null);
  const approve = useAction((id: string) => api.post(`/admin/billing/transfers/${id}/approve`, {}), [["billing"], ["admin"]]);
  const run = (p: Promise<unknown>, done: string) => p.then(() => setMsg(done)).catch((e) => setMsg(errorMessage(e)));
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={errorMessage(q.error)} />;
  const { transfers, taxInvoices } = q.data;
  return (
    <>
      {msg && <InfoRow title={msg} />}
      <Panel title="입금 신고" extra={<Badge tone={transfers.length ? "amber" : "teal"}>{transfers.length}건</Badge>}>
        {transfers.length === 0 && <InfoRow title="확인할 입금 신고가 없습니다" />}
        {transfers.map((t) => (
          <InfoRow
            key={t.id}
            title={`${t.customerName} · ${t.invoiceNumber} · ${won(t.declaredAmount)}${t.declaredAmount !== t.invoiceTotal ? ` (청구 ${won(t.invoiceTotal)})` : ""}`}
            description={
              <>
                입금자{" "}
                <MaskedValue
                  value={t.depositorName}
                  source={{ url: `/admin/billing/invoices/${t.invoiceId}/pii`, pick: (d) => (d.transfers as { id: string; depositorName: string }[] | undefined)?.find((x) => x.id === t.id)?.depositorName }}
                />
                {` · 입금 ${fmtDateTime(t.depositedAt)} · 신고 ${fmtDateTime(t.declaredAt)}`}
              </>
            }
          >
            {t.declaredAmount !== t.invoiceTotal && <Badge tone="amber">금액 다름</Badge>}
            {canManage && (
              <>
                <Button size="sm" onClick={() => void run(approve.mutateAsync(t.id), `${t.customerName} 입금을 승인했습니다. 플랜과 기간이 적용됐습니다.`)}>
                  승인
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setReject(t.id)}>
                  반려
                </Button>
              </>
            )}
          </InfoRow>
        ))}
      </Panel>
      <Panel title="세금계산서 요청" extra={<Badge tone={taxInvoices.length ? "amber" : "teal"}>{taxInvoices.length}건</Badge>}>
        {taxInvoices.length === 0 && <InfoRow title="발행할 세금계산서가 없습니다" />}
        {taxInvoices.map((t) => (
          <InfoRow key={t.id} title={`${t.customerName} · ${t.invoiceNumber} · 공급가액 ${won(t.supply)} · 세액 ${won(t.vat)}`} description={`${t.corpName} (${bizNoText(t.bizNo)}) · 대표 ${t.ceoName} · ${t.email}`}>
            {canManage && (
              <Button size="sm" variant="secondary" onClick={() => setIssue(t)}>
                발행 기록
              </Button>
            )}
          </InfoRow>
        ))}
      </Panel>
      <RejectDialog id={reject} onClose={() => setReject(null)} onDone={setMsg} />
      <IssueTaxDialog target={issue} onClose={() => setIssue(null)} onDone={setMsg} />
    </>
  );
}

function RejectDialog({ id, onClose, onDone }: { id: string | null; onClose: () => void; onDone: (m: string) => void }) {
  const [note, setNote] = useState("");
  const send = useAction(() => api.post(`/admin/billing/transfers/${id}/reject`, { note }), [["billing"]]);
  return (
    <Dialog
      open={!!id}
      onOpenChange={(v) => !v && onClose()}
      title="입금 신고 반려"
      description="반려 사유는 고객에게 보입니다. 청구서는 다시 미납이 됩니다."
      footer={
        <DialogActions
          secondary={{ label: "취소", onClick: onClose }}
          primary={{
            label: "반려",
            tone: "danger",
            disabled: !note.trim(),
            onClick: () =>
              void send
                .mutateAsync(undefined)
                .then(() => onDone("입금 신고를 반려했습니다."))
                .catch((e) => onDone(errorMessage(e)))
                .finally(onClose),
          }}
        />
      }
    >
      <Field label="반려 사유" value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 해당 입금 내역을 찾을 수 없습니다" />
    </Dialog>
  );
}

function IssueTaxDialog({ target, onClose, onDone }: { target: Approvals["taxInvoices"][number] | null; onClose: () => void; onDone: (m: string) => void }) {
  const formId = useId();
  const send = useAction((b: Record<string, unknown>) => api.post(`/admin/billing/tax-invoices/${target!.id}`, b), [["billing"]]);
  return (
    <Dialog
      open={!!target}
      onOpenChange={(v) => !v && onClose()}
      title="세금계산서 발행 기록"
      description="홈택스 등에서 발행한 뒤 국세청 승인번호를 입력하세요."
      footer={<DialogActions secondary={{ label: "닫기", onClick: onClose }} primary={{ label: "기록", type: "submit", form: formId }} />}
    >
      {target && (
        <form
          id={formId}
          className="mcc-form"
          onSubmit={(e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            void send
              .mutateAsync({ status: "issued", approvalNo: String(d.get("approvalNo")).trim(), issuedAt: new Date(String(d.get("issuedAt"))).toISOString() })
              .then(() => onDone("세금계산서 발행을 기록했습니다."))
              .catch((er) => onDone(errorMessage(er)))
              .finally(onClose);
          }}
        >
          <dl className="mcc-kv">
            <dt>공급받는자</dt>
            <dd>
              {target.corpName} ({bizNoText(target.bizNo)}) · 대표{" "}
              <MaskedValue
                value={target.ceoName}
                source={{ url: `/admin/billing/invoices/${target.invoiceId}/pii`, pick: (d) => (d.taxInvoices as { id: string; ceoName: string }[] | undefined)?.find((x) => x.id === target.id)?.ceoName }}
              />
            </dd>
            <dt>주소·업태</dt>
            <dd>{[target.address, target.bizType].filter(Boolean).join(" · ") || "-"}</dd>
            <dt>금액</dt>
            <dd>
              공급가액 {won(target.supply)} · 세액 {won(target.vat)}
            </dd>
            <dt>받을 이메일</dt>
            <dd>
              <MaskedValue
                value={target.email}
                source={{ url: `/admin/billing/invoices/${target.invoiceId}/pii`, pick: (d) => (d.taxInvoices as { id: string; email: string }[] | undefined)?.find((x) => x.id === target.id)?.email }}
              />
            </dd>
          </dl>
          <Field name="approvalNo" label="국세청 승인번호" required />
          <Field name="issuedAt" label="발행일" type="date" defaultValue={new Date().toISOString().slice(0, 10)} required />
        </form>
      )}
    </Dialog>
  );
}

function InvoicesPanel({ me }: { me: Me }) {
  const [status, setStatus] = useState("");
  const [customerId, setCustomerId] = useState("");
  const customers = useCustomers();
  const q = useAdminInvoices({ status, customerId });
  const [issueOpen, setIssueOpen] = useState(false);
  const [confirm, setConfirm] = useState<Invoice | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const voidInv = useAction((id: string) => api.post(`/admin/billing/invoices/${id}/void`, { reason: "관리자 취소" }), [["billing"]]);
  const canManage = can(me, "BILLING_MANAGE");
  return (
    <>
      <FilterBar>
        <Select label="상태" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">전체</option>
          <option value="open,overdue">미납</option>
          <option value="pending_confirmation">입금 확인 중</option>
          <option value="paid">결제 완료</option>
          <option value="void">취소</option>
        </Select>
        <Select label="고객사" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">전체 고객사</option>
          {(customers.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        {canManage && <Button onClick={() => setIssueOpen(true)}>청구서 발행</Button>}
      </FilterBar>
      {msg && <InfoRow title={msg} />}
      {q.isLoading ? (
        <Loading />
      ) : (q.data?.items ?? []).length === 0 ? (
        <Empty>청구서가 없습니다.</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>청구서</th>
              <th>고객사</th>
              <th>금액</th>
              <th>기한 / 결제</th>
              <th>상태</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(q.data?.items ?? []).map((inv) => (
              <tr key={inv.id}>
                <td>
                  {inv.number}
                  <small className="meta-crew-sub">
                    {inv.planName ?? inv.planCode} · {inv.period === "year" ? "연간" : "월간"}
                  </small>
                </td>
                <td>{inv.customerName}</td>
                <td>
                  {won(inv.total)}
                  <small className="meta-crew-sub">VAT {won(inv.vat)}</small>
                </td>
                <td>
                  {fmtDate(inv.dueAt)}
                  <small className="meta-crew-sub">{inv.paidAt ? `결제 ${fmtDate(inv.paidAt)}` : ""}</small>
                </td>
                <td>
                  <InvoiceStatusBadge inv={inv} />
                  {inv.taxInvoice && <small className="meta-crew-sub">세금계산서 {inv.taxInvoice.status === "issued" ? "발행" : "요청"}</small>}
                </td>
                <td>
                  {canManage && (inv.status === "open" || inv.status === "overdue") && (
                    <div className="mcc-row-actions">
                      <Button size="sm" variant="secondary" onClick={() => setConfirm(inv)}>
                        입금 확인
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void voidInv.mutateAsync(inv.id).then(() => setMsg(`${inv.number}을 취소했습니다.`)).catch((e) => setMsg(errorMessage(e)))}>
                        취소
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <IssueInvoiceDialog open={issueOpen} onClose={() => setIssueOpen(false)} onDone={setMsg} />
      <DeclareDialog invoice={confirm} path={(id) => `/admin/billing/invoices/${id}/confirm-transfer`} onClose={() => setConfirm(null)} title="입금 확인 (신고 없이 승인)" confirmLabel="승인" />
    </>
  );
}

function IssueInvoiceDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (m: string) => void }) {
  const formId = useId();
  const customers = useCustomers();
  const plans = usePlans();
  const [error, setError] = useState<string | null>(null);
  const [planKey, setPlanKey] = useState("basic:month");
  const [adjust, setAdjust] = useState(0);
  const send = useAction((v: { customerId: string; body: Record<string, unknown> }) => api.post<{ number: string; total: number }>(`/admin/billing/customers/${v.customerId}/invoices`, v.body), [["billing"]]);
  const plan = (plans.data?.items ?? []).find((p) => `${p.planCode}:${p.period}` === planKey);
  const supply = (plan?.price ?? 0) + adjust;
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="청구서 발행"
      description="결제가 확인되면 플랜과 기간이 자동 적용되고, 정지 중이면 다시 열립니다."
      footer={<DialogActions secondary={{ label: "취소", onClick: onClose }} primary={{ label: "발행", type: "submit", form: formId, disabled: send.isPending }} note={error ?? undefined} />}
    >
      <form
        id={formId}
        className="mcc-form"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const d = new FormData(e.currentTarget);
          const [planCode, period] = planKey.split(":");
          const adjustments = adjust ? [{ label: String(d.get("adjustLabel") || "조정"), amount: adjust }] : undefined;
          send
            .mutateAsync({ customerId: String(d.get("customerId")), body: { planCode, period, memo: String(d.get("memo") || "") || undefined, adjustments } })
            .then((r) => {
              onDone(`${r.number} 발행 · 합계 ${won(r.total)}`);
              onClose();
            })
            .catch((er) => setError(errorMessage(er)));
        }}
      >
        <Select label="고객사" name="customerId" required>
          {(customers.data?.items ?? [])
            .filter((c) => c.status !== "terminated")
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
        </Select>
        <Select label="플랜" value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
          {(plans.data?.items ?? [])
            .filter((p) => p.planCode !== "trial" && p.billable !== false)
            .map((p) => (
              <option key={`${p.planCode}:${p.period}`} value={`${p.planCode}:${p.period}`}>
                {p.name} · {p.period === "year" ? "연간" : "월간"} · {won(p.price)}
              </option>
            ))}
        </Select>
        <Field name="adjustLabel" label="조정 항목 이름" placeholder="예: 첫 달 할인 / 기본 이용료" />
        <Field label="조정 금액 (원, 할인은 음수)" type="number" value={adjust} onChange={(e) => setAdjust(Number(e.target.value) || 0)} />
        <Field name="memo" label="메모 (고객에게 보임)" />
        <InfoRow title={`공급가액 ${won(supply)} + 부가세`} description="부가세율은 정책 설정 값으로 계산됩니다. 0원 청구서는 발행할 수 없습니다." />
      </form>
    </Dialog>
  );
}

function SubscriptionsPanel({ enter }: { enter: (id: string, s?: string) => void }) {
  const q = useCustomers();
  const list = q.data?.items ?? [];
  return (
    <Panel title="고객사 구독">
      <Table>
        <thead>
          <tr>
            <th>고객사</th>
            <th>플랜</th>
            <th>기간 종료</th>
            <th>이번 달 작업</th>
            <th>상태</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>{c.stats?.plan ? `${c.stats.plan.planName} · ${c.stats.plan.period === "year" ? "연간" : "월간"}` : "-"}</td>
              <td>{fmtDate(c.stats?.plan?.currentPeriodEnd)}</td>
              <td>
                {num(c.quota.used)}
                {c.quota.tasksPerMonth ? ` / ${num(c.quota.tasksPerMonth)}` : ""}
              </td>
              <td>
                <Badge tone={serviceTone[c.status]}>{serviceNames[c.status]}</Badge>
              </td>
              <td>
                <Button size="sm" variant="ghost" onClick={() => enter(c.id, "billing")}>
                  결제 화면
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
}

/** 플랜 가격: 값을 바꾸면 새 버전 (기존 구독은 그대로, 새 청구서부터 적용). */
function PlanPricePanel({ me }: { me: Me }) {
  const q = usePlans();
  const [msg, setMsg] = useState<string | null>(null);
  const save = useAction((p: Plan & { price: number }) => api.post("/admin/plans", { planCode: p.planCode, period: p.period, name: p.name, tier: p.tier, price: p.price, includes: p.includes, overagePolicy: p.overagePolicy, aiPolicy: (p as unknown as { aiPolicy: string }).aiPolicy, applyMode: "new_only" }), [["admin", "plans"]]);
  const canManage = can(me, "BILLING_MANAGE");
  return (
    <Panel title="플랜 가격 (공급가액, VAT 별도)">
      {msg && <InfoRow title={msg} />}
      {(q.data?.items ?? [])
        .filter((p) => p.planCode !== "trial" && p.billable !== false)
        .map((p) => (
          <InfoRow key={`${p.planCode}-${p.period}`} title={`${p.name} · ${p.period === "year" ? "연간" : "월간"} · v${p.version}`} description={`월 작업 ${p.includes.tasksPerMonth ?? "무제한"}건 · 머신 ${p.includes.machines ?? "무제한"} · 디바이스 ${p.includes.devices ?? "무제한"}`}>
            {canManage ? (
              <form
                className="meta-crew-buttons"
                onSubmit={(e) => {
                  e.preventDefault();
                  const price = Number(new FormData(e.currentTarget).get("price"));
                  void save
                    .mutateAsync({ ...p, price })
                    .then(() => setMsg(`${p.name} ${p.period === "year" ? "연간" : "월간"} 가격을 ${won(price)}으로 바꿨습니다 (새 청구서부터).`))
                    .catch((er) => setMsg(errorMessage(er)));
                }}
              >
                <input className="input" name="price" type="number" min={0} defaultValue={p.price} aria-label={`${p.name} 가격`} style={{ width: 140 }} />
                <Button size="sm" variant="secondary" type="submit">
                  저장
                </Button>
              </form>
            ) : (
              <Badge>{won(p.price)}</Badge>
            )}
          </InfoRow>
        ))}
    </Panel>
  );
}
